import type { FcTable } from "./factcheck.js";
/**
 * Types côté UI qui complètent `engine/src/types.ts` (jamais de chiffre ici).
 * - `FinancingLever` : métadonnées d'un curseur de financement (content/levers.json), le moteur ne connaît que
 *   `Financing.revenue_raises[id] = Md€`.
 * - `UiMeasure` : alias de `Measure` (décomposition, variantes et description sont dans le type moteur v0.2).
 */
import type {
  Baseline,
  EventOption,
  Figure,
  Financing,
  GameEvent,
  Measure,
  MeasureBreakdownItem,
  MeasureCategory,
  MeasureEffects,
  MeasureStatus,
  MeasureVariant,
  ParamSet,
  Programme,
  ScorecardDefs,
  Source,
} from "../../engine/src/types.js";
import type { PoliticalNames } from "./neutrality.js";
import type { ParamSensitivity, ParamSources } from "./params-info.js";

export interface FinancingLever {
  id: string;
  label: string;
  /** Une phrase : assiette, fuite, effets secondaires. */
  hint?: string;
  /** Borne basse (Md€, négatif = baisse d'impôt) — params `lev_<id>_min_bn`. */
  min: Figure;
  /** Md€ en régime mobilisables au maximum par ce curseur — params `lev_<id>_max_bn`. */
  max: Figure;
  /** Pas du curseur en Md€ (défaut : 1). */
  step?: number;
  /** Part de fuite / évitement par Md€ (ratio) ; appliquée par le moteur, affichée par l'UI. */
  leak: Figure;
  /** Capital politique consommé par Md€ levé. */
  political_cost_per_bn: Figure;
  /** Effets par Md€ levé (informatif). */
  fx?: MeasureEffects;
  sources: Source[];
  status: MeasureStatus;
}

/** Poste de décomposition et variante en unités physiques : types du moteur (contenu v0.2, PRD §14.4). */
export type BreakdownItem = MeasureBreakdownItem;
export type { MeasureVariant };

export type UiMeasure = Measure;

/** Financement proposé d'une option de crise (H2) — optionnel ; absent = la dépense part en dette. */
export interface OptionFinancing {
  /** Part financée par des recettes ou économies (Md€). */
  revenue_bn?: number;
  /** Part laissée à la dette (Md€). */
  debt_bn?: number;
  note?: string;
}

export type UiEventOption = EventOption & { financing?: OptionFinancing };

/**
 * Événement v0.2 : `shock` (moteur) = choc macro subi quelle que soit la réponse (récession, inflation…).
 * v0.3 (supposé) : `reference_option_id` = réponse de référence (« réponse minimale ») à laquelle les autres sont comparées.
 */
export type UiGameEvent = Omit<GameEvent, "options"> & { options: UiEventOption[]; reference_option_id?: string };

/**
 * Financement côté UI : contrat moteur + économies ciblées par groupe de missions (v0.3, supposé :
 * `financing.mission_cuts[groupe] = %`, bornes `cut_<groupe>_max_pct`). Transmis tel quel ; ignoré par un moteur qui ne le lit pas.
 */
export type UiFinancing = Financing & { mission_cuts?: Record<string, number> };

/** Programme côté UI = programme moteur (variantes lues nativement : `programme.variants[mesure] = variante`). */
export type UiProgramme = Programme;

export type ContentOrigin = "file" | "fixture" | "merged";

/** Programme préréglé (schéma : scripts/src/schemas/presets.ts). */
export interface UiPreset {
  id: string;
  label: string;
  description: string;
  measure_ids: string[];
  financing: UiFinancing;
  sources: Source[];
  note?: string;
}

export interface Content {
  baseline: Baseline;
  params: ParamSet;
  measures: UiMeasure[];
  events: UiGameEvent[];
  levers: FinancingLever[];
  /** Chemins de la baseline servis par la fixture (source TODO) après fusion : affichés « non sourcé ». */
  unsourcedPaths: string[];
  /** Liste du filtre de neutralité (`content/policy/political-names.json`) ; null si absente (mesures de lien refusées). */
  politicalNames: PoliticalNames | null;
  /** Sources des paramètres (`content/params.sources.json`, v0.3) ; null si absent. */
  paramSources: ParamSources | null;
  /** Sensibilité exportée (`content/params.sensitivity.json`, optionnel). */
  sensitivity: ParamSensitivity | null;
  /** Programmes préréglés (`content/presets.json`, ADR 0014 §3), ex. « Le budget du gouvernement (PLF 2027) ». */
  presets: UiPreset[];
  /** Badges de fact-check par chiffre (`content/factcheck.status.json`, calculé au build) ; null si absent. */
  factcheck: FcTable | null;
  /** Bilan chiffré comparé (`content/scorecard.json`, ADR 0015) ; null si absent (repli : chiffres clés). */
  scorecard: ScorecardDefs | null;
  origin: Record<"baseline" | "params" | "measures" | "events" | "levers", ContentOrigin>;
}

/** Libellés français des catégories (vocabulaire, pas des chiffres). Ordre d'affichage du catalogue. */
export const CATEGORY_LABELS: Record<MeasureCategory, string> = {
  retraites: "Retraites",
  sante: "Santé",
  ecole: "École & jeunesse",
  securite: "Sécurité & justice",
  immigration: "Immigration",
  fiscalite_menages: "Fiscalité des ménages",
  fiscalite_entreprises: "Fiscalité des entreprises",
  energie: "Énergie & climat",
  logement: "Logement",
  travail: "Travail & pouvoir d'achat",
  defense: "Défense",
  etat: "État & fonction publique",
  europe_dette: "Europe & dette",
};

export const STATUS_LABELS: Record<MeasureStatus, string> = {
  sourced: "sourcé",
  non_chiffré: "non chiffré",
  estimé_ia: "Estimation IA — non sourcée",
  estimé_ia_source: "Chiffrage IA sourcé — non vérifié",
  estimé_ia_une_source: "Chiffrage IA — une seule source",
};
